import { useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Send, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useRecorder } from "@/hooks/useRecorder";
import { transcribeAudio, extractText } from "@/lib/pocApi";
import { RecorderPanel } from "@/components/poc/RecorderPanel";
import { ExtractionView } from "@/components/poc/ExtractionView";
import { JsonPanel } from "@/components/poc/JsonPanel";
import { NamtechFooter } from "@/components/poc/NamtechFooter";

const SAMPLE = "Hoy hablé con Pedro Ramírez de Constructora Norte, necesita un despacho fiscal, le voy a presentar a Laura, comisión del 10% si cierran, siguiente paso llamarlo el viernes";

const Card = ({ title, badge, children, testid }) => (
  <section data-testid={testid} className="fade-up rounded-xl border border-zinc-800 bg-[#121215] p-4 space-y-3">
    <div className="flex items-center justify-between">
      <h2 className="font-display text-xl font-bold uppercase tracking-wide">{title}</h2>
      {badge && <span className="font-mono text-[10px] text-cyan-400 bg-zinc-900 border border-zinc-800 px-2 py-0.5 rounded">{badge}</span>}
    </div>
    {children}
  </section>
);

export default function PocPage() {
  const rec = useRecorder();
  const [stage, setStage] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [transcript, setTranscript] = useState(null);
  const [extraction, setExtraction] = useState(null);
  const [text, setText] = useState("");
  const busy = stage !== null;

  const runExtract = async (input, runId) => {
    setStage("extracting");
    const res = await extractText(input, runId);
    setExtraction(res);
    toast.success("Extracción lista");
  };

  const processAudio = async (blob, filename) => {
    setExtraction(null);
    setTranscript(null);
    setAudioUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
    try {
      setStage("transcribing");
      toast("Enviando audio…");
      const t = await transcribeAudio(blob, filename);
      setTranscript(t);
      if (!t.text) {
        toast.error("No se entendió el audio. Intenta de nuevo.");
        return;
      }
      await runExtract(t.text, t.run_id);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStage(null);
    }
  };

  const onStop = async () => {
    const out = await rec.stop();
    if (!out || out.blob.size < 1000) return toast.error("Grabación vacía o demasiado corta.");
    toast.success("Grabación completada");
    processAudio(out.blob, `nota.${out.ext}`);
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (f) processAudio(f, f.name);
    e.target.value = "";
  };

  const onExtractText = async () => {
    if (!text.trim()) return toast.error("Escribe o pega un texto.");
    setExtraction(null);
    setTranscript(null);
    try { await runExtract(text.trim()); } catch (e) { toast.error(e.message); } finally { setStage(null); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-zinc-100">
    <div data-testid="poc-page" className="max-w-md mx-auto min-h-screen px-4 pt-6 flex flex-col gap-5 bg-[#0a0a0b]">
      <Link to="/conecta/grabar" data-testid="poc-back-link" className="safe-top text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-cyan-400">← Volver a CONECTA</Link>
      <header className="space-y-1">
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#bc9538]">Fase 0 · Prueba de concepto</span>
        <h1 data-testid="poc-title" className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight leading-none">Conecta</h1>
        <p className="text-sm text-zinc-400">Voz → transcripción (Whisper) → extracción (Claude). Nada se guarda como definitivo.</p>
      </header>

      <RecorderPanel state={rec.state} elapsed={rec.elapsed} busy={busy} mimeType={rec.mimeType} error={rec.error} onStart={() => { rec.start(); }} onStop={onStop} />

      <label data-testid="upload-audio-label" className="flex items-center justify-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-cyan-400 cursor-pointer transition-colors">
        <Upload className="w-4 h-4" /> o sube un archivo de audio
        <input data-testid="upload-audio-input" type="file" accept="audio/*,video/mp4,.m4a,.webm,.mp4,.aac" className="hidden" onChange={onFile} disabled={busy} />
      </label>

      {stage && (
        <div data-testid="pipeline-stage" className="flex items-center gap-2 text-sm text-cyan-400 font-mono">
          <Loader2 className="w-4 h-4 animate-spin" /> {stage === "transcribing" ? "Transcribiendo…" : "Extrayendo estructura…"}
        </div>
      )}

      {audioUrl && (
        <Card title="Audio" testid="audio-card">
          <audio data-testid="audio-player" controls src={audioUrl} className="w-full" />
        </Card>
      )}

      {transcript && (
        <Card title="Transcripción" badge={transcript.model} testid="transcription-card">
          <p data-testid="transcription-text" className="text-zinc-200 leading-relaxed">{transcript.text || <span className="text-zinc-600 font-mono">(vacío: {transcript.rejected_reason})</span>}</p>
          <p data-testid="transcription-meta" className="font-mono text-[10px] text-zinc-500 break-all">{transcript.content_type} · {(transcript.size / 1024).toFixed(1)} KB · {transcript.storage_path}</p>
        </Card>
      )}

      {extraction && (
        <>
          <ExtractionView data={extraction.extraction} model={extraction.model} />
          <JsonPanel data={extraction.extraction} />
        </>
      )}

      <Card title="Extraer desde texto" testid="text-extract-card">
        <textarea
          data-testid="extract-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Pega aquí una nota o transcripción…"
          className="w-full bg-[#0a0a0b] border border-zinc-800 rounded-lg p-3 text-zinc-100 placeholder-zinc-600 focus:outline-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 min-h-[110px]"
        />
        <div className="flex gap-2">
          <button data-testid="extract-sample-btn" onClick={() => setText(SAMPLE)} className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-zinc-800 text-zinc-300 text-sm hover:border-zinc-600 transition-colors">
            <FileText className="w-4 h-4" /> Ejemplo
          </button>
          <button data-testid="extract-text-btn" onClick={onExtractText} disabled={busy} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#00e5ff] text-black font-display font-bold uppercase tracking-wider hover:bg-[#00b4d8] active:scale-[0.98] transition-[transform,background-color] disabled:opacity-50">
            {stage === "extracting" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Extraer
          </button>
        </div>
      </Card>

      <NamtechFooter tone="dark" className="!border-zinc-900 !text-zinc-500" />
    </div>
    </div>
  );
}
